// Shared Date Range Filter for the CX module (iter176).
// Presets cover daily reviews → monthly business analysis. Selection
// persists across tab navigation via localStorage so a manager doing a
// "Previous Month" review doesn't lose their context when bouncing
// between Overview · Analytics · Submissions.
import { useState, useEffect, useRef } from 'react';
import { Calendar as CalendarIcon, ChevronDown } from 'lucide-react';

const STORAGE_KEY = 'cx_date_range_v1';

const PRESETS = [
  { v: 'today',          label: 'Today' },
  { v: 'yesterday',      label: 'Yesterday' },
  { v: 'last_7_days',    label: 'Last 7 days' },
  { v: 'last_30_days',   label: 'Last 30 days' },
  { v: 'this_month',     label: 'This month' },
  { v: 'previous_month', label: 'Previous month' },
  { v: 'last_90_days',   label: 'Last 90 days' },
  { v: 'custom',         label: 'Custom range…' },
];

const fmtISO = (d) => d.toISOString().slice(0, 10);

/** Resolve a preset key → { start_date, end_date, days }. */
export function resolveRange(preset, custom = {}) {
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  const startOf = (date) => { const d = new Date(date); d.setUTCHours(0, 0, 0, 0); return d; };
  const addDays = (date, n) => { const d = new Date(date); d.setUTCDate(d.getUTCDate() + n); return d; };

  if (preset === 'today') {
    return { start_date: fmtISO(today), end_date: fmtISO(today), days: 1 };
  }
  if (preset === 'yesterday') {
    const y = addDays(today, -1);
    return { start_date: fmtISO(y), end_date: fmtISO(y), days: 1 };
  }
  if (preset === 'last_7_days') {
    return { start_date: fmtISO(addDays(today, -6)), end_date: fmtISO(today), days: 7 };
  }
  if (preset === 'last_30_days') {
    return { start_date: fmtISO(addDays(today, -29)), end_date: fmtISO(today), days: 30 };
  }
  if (preset === 'last_90_days') {
    return { start_date: fmtISO(addDays(today, -89)), end_date: fmtISO(today), days: 90 };
  }
  if (preset === 'this_month') {
    const start = startOf(new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1)));
    return { start_date: fmtISO(start), end_date: fmtISO(today),
             days: Math.max(1, Math.round((today - start) / 86400000) + 1) };
  }
  if (preset === 'previous_month') {
    const start = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - 1, 1));
    const end = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 0));
    return { start_date: fmtISO(start), end_date: fmtISO(end),
             days: Math.max(1, Math.round((end - start) / 86400000) + 1) };
  }
  if (preset === 'custom' && custom.start_date && custom.end_date) {
    const s = new Date(custom.start_date), e = new Date(custom.end_date);
    return {
      start_date: custom.start_date, end_date: custom.end_date,
      days: Math.max(1, Math.round((e - s) / 86400000) + 1),
    };
  }
  // Fallback
  return resolveRange('last_30_days');
}

export function loadSavedRange() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { preset: 'last_30_days', custom: {} };
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') return parsed;
  } catch { /* corrupt JSON — ignore */ }
  return { preset: 'last_30_days', custom: {} };
}

function saveRange(state) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* quota */ }
}

export default function CxDateRangeFilter({ value, onChange, testId = 'cx-date-range' }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const preset = value?.preset || 'last_30_days';
  const custom = value?.custom || {};
  const resolved = resolveRange(preset, custom);

  // Click-outside to close dropdown.
  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const apply = (next) => {
    saveRange(next);
    onChange?.({ ...next, resolved: resolveRange(next.preset, next.custom) });
  };
  const pick = (p) => {
    if (p === 'custom') { apply({ preset: 'custom', custom }); return; }
    apply({ preset: p, custom: {} });
    setOpen(false);
  };

  const label = PRESETS.find((p) => p.v === preset)?.label || 'Date range';
  const dateLabel = preset === 'custom'
    ? (custom.start_date && custom.end_date
        ? `${custom.start_date} → ${custom.end_date}` : 'Custom range…')
    : `${resolved.start_date} → ${resolved.end_date}`;

  return (
    <div ref={wrapRef} className="relative inline-block" data-testid={testId}>
      <button type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 h-9 px-3 rounded-md border border-[#E2E8DF] bg-white hover:border-[color:var(--color-primary)] text-[12px] text-[#1A2421] transition"
        data-testid={`${testId}-trigger`}>
        <CalendarIcon className="w-3.5 h-3.5 text-[#5C6B64]" />
        <span className="font-medium">{label}</span>
        <span className="text-[10.5px] text-[#5C6B64]">{dateLabel}</span>
        <ChevronDown className="w-3 h-3 text-[#5C6B64]" />
      </button>
      {open && (
        <div className="absolute right-0 mt-1.5 z-30 bg-white border border-[#E2E8DF] rounded-md shadow-lg p-2 min-w-[300px]"
             data-testid={`${testId}-menu`}>
          <div className="grid grid-cols-2 gap-1">
            {PRESETS.map((p) => (
              <button key={p.v} type="button"
                onClick={() => pick(p.v)}
                className={`text-left text-[11.5px] px-2.5 py-1.5 rounded transition ${
                  preset === p.v
                    ? 'bg-[color:var(--color-primary)] text-white'
                    : 'text-[#1A2421] hover:bg-[#F4F7F4]'}`}
                data-testid={`${testId}-preset-${p.v}`}>
                {p.label}
              </button>
            ))}
          </div>
          {preset === 'custom' && (
            <div className="border-t border-[#E2E8DF] mt-2 pt-2 space-y-1.5">
              <div className="text-[10px] uppercase tracking-wider text-[#5C6B64] font-medium">
                Custom range
              </div>
              <div className="flex gap-2 items-center">
                <input type="date" value={custom.start_date || ''}
                  onChange={(e) => apply({ preset: 'custom', custom: { ...custom, start_date: e.target.value } })}
                  className="flex-1 h-8 px-2 text-[11.5px] border border-[#E2E8DF] rounded"
                  data-testid={`${testId}-custom-start`} />
                <span className="text-[11px] text-[#5C6B64]">→</span>
                <input type="date" value={custom.end_date || ''}
                  onChange={(e) => apply({ preset: 'custom', custom: { ...custom, end_date: e.target.value } })}
                  className="flex-1 h-8 px-2 text-[11.5px] border border-[#E2E8DF] rounded"
                  data-testid={`${testId}-custom-end`} />
              </div>
              {custom.start_date && custom.end_date && new Date(custom.end_date) < new Date(custom.start_date) && (
                <div className="text-[10.5px] text-[#C45A41]">End date must be on or after start date.</div>
              )}
              <button type="button"
                onClick={() => setOpen(false)}
                disabled={!custom.start_date || !custom.end_date}
                className="w-full mt-1 h-8 bg-[color:var(--color-primary)] text-white text-[11.5px] rounded disabled:opacity-50"
                data-testid={`${testId}-custom-apply`}>
                Apply range
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
